<template>
  <div class="accessibility-page">
    <!-- Contenido principal -->
    <div class="main-content">
      <div class="content-card">
        <!-- Declaración -->
        <section class="content-section">
          <p class="text-body-1 section-text">
            {{ t("accesibilidad.declaracion.texto") }}
          </p>
          <p class="text-body-1 section-text">
            {{ t("accesibilidad.versiones.texto") }}
          </p>

          <!-- Versiones de apps -->
          <div class="info-box mb-6">
            <div class="info-item">
              <v-icon
                color="primary"
                aria-hidden="true"
              >
                mdi-android
              </v-icon>
              <span>{{ t("accesibilidad.versiones.android") }}</span>
            </div>
            <div class="info-item">
              <v-icon
                color="primary"
                aria-hidden="true"
              >
                mdi-apple
              </v-icon>
              <span>{{ t("accesibilidad.versiones.ios") }}</span>
            </div>
          </div>
        </section>

        <!-- Cumplimiento -->
        <section class="content-section">
          <h2 class="section-title">
            {{ t("accesibilidad.cumplimiento.title") }}
          </h2>
          <p class="text-body-1 section-text">
            {{ t("accesibilidad.cumplimiento.texto") }}
          </p>
        </section>

        <!-- Contenido no accesible -->
        <section class="content-section">
          <h2 class="section-title">
            {{ t("accesibilidad.contenido_no_accesible.title") }}
          </h2>
          <p class="text-body-1 section-text">
            {{ t("accesibilidad.contenido_no_accesible.texto") }}
          </p>

          <!-- Lista de problemas -->
          <div class="warning-box mb-6">
            <div
              v-for="i in 6"
              :key="i"
              class="warning-item"
            >
              <v-icon
                color="warning"
                size="small"
                aria-hidden="true"
              >
                mdi-alert-circle
              </v-icon>
              <span>{{
                t(`accesibilidad.contenido_no_accesible.item${i}`)
              }}</span>
            </div>
          </div>
        </section>

        <!-- Preparación -->
        <section class="content-section">
          <h2 class="section-title">
            {{ t("accesibilidad.preparacion.title") }}
          </h2>
          <p class="text-body-1 section-text">
            {{ t("accesibilidad.preparacion.texto") }}
          </p>
          <p class="text-body-1 section-text">
            {{ t("accesibilidad.preparacion.revision") }}
          </p>
        </section>

        <!-- Contacto -->
        <section class="content-section">
          <h2 class="section-title">
            {{ t("accesibilidad.contacto.title") }}
          </h2>
          <p class="text-body-1 section-text">
            {{ t("accesibilidad.contacto.texto") }}
          </p>

          <address class="contact-box mb-6">
            <div class="contact-item">
              <v-icon
                color="primary"
                aria-hidden="true"
              >
                mdi-email
              </v-icon>
              <div>
                <strong>Email:</strong>
                <a
                  href="mailto:accesibilidad@cnmc.es"
                  class="contact-link"
                >
                  accesibilidad@cnmc.es
                </a>
              </div>
            </div>

            <div class="contact-item">
              <v-icon
                color="primary"
                aria-hidden="true"
              >
                mdi-phone
              </v-icon>
              <div>
                <strong>{{ t("accesibilidad.contacto.telefono") }}:</strong>
                <a
                  href="tel:+34914329600"
                  class="contact-link"
                >
                  +34 914 329 600
                </a>
              </div>
            </div>

            <div class="contact-item">
              <v-icon
                color="primary"
                aria-hidden="true"
              >
                mdi-form-select
              </v-icon>
              <div>
                <strong>{{ t("accesibilidad.contacto.formulario") }}:</strong>
                <a
                  href="https://sede.cnmc.gob.es/tramites/general/solicitudes-de-informacion-quejas-accesibilidad"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="contact-link"
                >
                  {{
                    t("accesibilidad.contacto.formularioEnlace") ||
                      "Formulario de contacto"
                  }}
                  <v-icon
                    size="x-small"
                    class="ml-1"
                    aria-hidden="true"
                  >mdi-open-in-new</v-icon>
                </a>
              </div>
            </div>
          </address>
        </section>

        <!-- Configuración móvil -->
        <section class="content-section">
          <h2 class="section-title">
            {{ t("accesibilidad.configuracion_movil.title") }}
          </h2>
          <p class="text-body-1 section-text">
            {{ t("accesibilidad.configuracion_movil.texto") }}
          </p>

          <nav
            class="link-box mb-6"
            :aria-label="
              t('accesibilidad.configuracion_movil.ariaLabel') ||
                'Enlaces de accesibilidad móvil'
            "
          >
            <a
              href="https://support.google.com/accessibility/"
              target="_blank"
              rel="noopener noreferrer"
              class="link-item"
            >
              <v-icon
                color="primary"
                aria-hidden="true"
              >mdi-android</v-icon>
              <span>{{
                t("accesibilidad.configuracion_movil.android")
              }}</span>
              <v-icon
                size="small"
                aria-hidden="true"
              >mdi-open-in-new</v-icon>
            </a>

            <a
              href="https://www.apple.com/accessibility/"
              target="_blank"
              rel="noopener noreferrer"
              class="link-item"
            >
              <v-icon
                color="primary"
                aria-hidden="true"
              >mdi-apple</v-icon>
              <span>{{ t("accesibilidad.configuracion_movil.ios") }}</span>
              <v-icon
                size="small"
                aria-hidden="true"
              >mdi-open-in-new</v-icon>
            </a>
          </nav>
        </section>

        <!-- Intermediación -->
        <section class="content-section">
          <h2 class="section-title">
            {{ t("accesibilidad.intermediacion.title") }}
          </h2>
          <p class="text-body-1 section-text">
            {{ t("accesibilidad.intermediacion.texto") }}
          </p>
          <p class="text-body-1 section-text">
            <v-icon
              color="primary"
              size="small"
              class="mr-2"
              aria-hidden="true"
            >
              mdi-link
            </v-icon>
            <strong>{{ t("accesibilidad.intermediacion.link") }}:</strong>
            <a
              href="https://www.dsca.gob.es/es/derechos-sociales/discapacidad/servicios/servicios-CIT"
              target="_blank"
              rel="noopener noreferrer"
              class="contact-link"
            >
              www.citcentro.com
              <v-icon
                size="x-small"
                class="ml-1"
                aria-hidden="true"
              >mdi-open-in-new</v-icon>
            </a>
          </p>
        </section>

        <!-- Versión de aplicación -->
        <section class="content-section">
          <h2 class="section-title">
            {{ t("accesibilidad.version_aplicacion.title") }}
          </h2>
          <p class="text-body-1 section-text">
            {{ t("accesibilidad.version_aplicacion.texto") }}
          </p>
        </section>

        <!-- Política de accesibilidad -->
        <section class="content-section">
          <h2 class="section-title">
            {{ t("accesibilidad.politica_accesibilidad.title") }}
          </h2>
          <p class="text-body-1 section-text">
            {{ t("accesibilidad.politica_accesibilidad.texto") }}
          </p>
        </section>

        <!-- Normativa y estándares -->
        <section class="content-section last">
          <h2 class="section-title">
            {{ t("accesibilidad.normativa_estandares.title") }}
          </h2>
          <p class="section-text last">
            {{ t("accesibilidad.normativa_estandares.texto") }}
          </p>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from "vue-i18n";
const { t } = useI18n();
</script>

<style scoped>
.accessibility-page {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
}

/* ===== Main Content ===== */
.main-content {
  padding: 16px 0 24px;
}

.content-card {
  background: linear-gradient(135deg, #f8fafc 0%, #fff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 28px;
}

/* ===== Content Sections ===== */
.content-section {
  margin-bottom: 28px;
}

.content-section.last {
  margin-bottom: 0;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 14px;
}

.section-text {
  font-size: 0.875rem;
  color: #475569;
  margin-bottom: 14px;
  line-height: 1.7;
}

.section-text.last {
  margin-bottom: 0;
}

/* ===== Info Box ===== */
.info-box {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px;
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
  border-radius: 10px;
  border: 1px solid #f0f0f0;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  color: #475569;
}

.info-item .v-icon {
  opacity: 0.8;
}

/* ===== Warning Box ===== */
.warning-box {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-radius: 10px;
  border: 1px solid #fde68a;
}

.warning-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.8125rem;
  color: #92400e;
  line-height: 1.5;
}

.warning-item .v-icon {
  margin-top: 1px;
  flex-shrink: 0;
  opacity: 0.8;
}

/* ===== Contact Box ===== */
.contact-box {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px;
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  font-style: normal;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.875rem;
  color: #475569;
}

.contact-item .v-icon {
  opacity: 0.8;
}

.contact-link {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.15s ease;
}

.contact-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.contact-link:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
  border-radius: 2px;
}

/* ===== Link Box ===== */
.link-box {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.link-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  text-decoration: none;
  color: #475569;
  font-size: 0.875rem;
  transition: all 0.15s ease;
}

.link-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border-color: #e5e7eb;
}

.link-item:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.link-item .v-icon {
  opacity: 0.8;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {

  .main-content {
    padding: 16px;
  }

  .content-card {
    padding: 20px;
  }
}

@media (max-width: 600px) {
  .content-section {
    margin-bottom: 20px;
  }

  .section-title {
    font-size: 0.9375rem;
  }

  .section-text {
    font-size: 0.8125rem;
  }

  .content-card {
    padding: 16px;
  }
}
</style>
